import React, {
  useEffect,
  useState,
} from 'react'
import {
  Button,
  FormGroup,
  Tab,
  Tabs,
} from 'react-bootstrap'
import { FileText } from 'lucide-react'
import Select from 'react-select'
import { useQueryClient } from '@tanstack/react-query'
import { useAccounts } from '@data/accounts/accounts'
import Account from '@data/accounts/model/account'
import DevicesPaymentsOverview from './DevicesPaymentsOverview'
import InvoiceHistory from './InvoiceHistory'
import './Subscriptions.scss'
import { InvoiceQuickFilter } from './SubscriptionAlerts'

type TabKey = 'devices' | 'invoices'

const Subscriptions: React.FC = () => {
  const accounts = useAccounts()
  const queryClient = useQueryClient()
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null)
  const [activeTab, setActiveTab] = useState<TabKey>('devices')
  const [invoiceFilter, setInvoiceFilter] = useState<InvoiceQuickFilter>('all')

  useEffect(() => {
    if (accounts.length === 1) {
      setSelectedAccount(accounts[0])
      return
    }
    const updated = accounts.find((a) => a.id === selectedAccount?.id)
    if (updated) setSelectedAccount(updated)
  }, [accounts])

  const accountId = selectedAccount?.id ? Number(selectedAccount.id) : undefined

  const handleAccountChange = (account: Account | null) => {
    setSelectedAccount(account)
    if (account?.id) {
      const id = Number(account.id)
      queryClient.invalidateQueries({ queryKey: ['subscription-devices', id] })
      queryClient.invalidateQueries({ queryKey: ['invoice-history', id] })
    }
  }

  const showInvoiceHistory = (filter: InvoiceQuickFilter) => {
    setInvoiceFilter(filter)
    setActiveTab('invoices')
  }

  return (
    <div className='p-3'>
      {accounts.length > 1 && (
        <FormGroup className='mb-3'>
          <Select
            classNamePrefix='select'
            value={selectedAccount}
            onChange={(account: any) => handleAccountChange(account)}
            options={accounts}
            getOptionValue={(option: Account) => option.id}
            getOptionLabel={(option: Account) => option.accountName}
            isClearable
            placeholder='Select an account...'
          />
        </FormGroup>
      )}
      {!accountId ? (
        <p className='text-muted'>Please select an account to view subscription details.</p>
      ) : (
        <>
          {activeTab === 'devices' && (
            <div className='d-flex justify-content-end mb-3'>
              <Button variant='secondary' size='sm' onClick={() => showInvoiceHistory('all')}>
                <FileText size={14} className='me-1' />
                View complete invoice history
              </Button>
            </div>
          )}
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab((k as TabKey) ?? 'devices')}
            className='mb-3'
          >
            <Tab eventKey='devices' title='Devices & Payments'>
              <DevicesPaymentsOverview accountId={accountId} onViewInvoices={showInvoiceHistory} />
            </Tab>
            <Tab eventKey='invoices' title='Invoice History'>
              <InvoiceHistory accountId={accountId} initialFilter={invoiceFilter} />
            </Tab>
          </Tabs>
        </>
      )}
    </div>
  )
}

export default Subscriptions
